"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getImageUrl } from '@/app/lib/api';

// Helper function to parse the video link prop
const parseVideoLinks = (link: string | string[] | null | undefined): string[] => {
    if (!link) return [];
    if (Array.isArray(link)) return link;

    const raw = link.trim();
    if (raw.startsWith('[')) {
        try {
            const decoded = JSON.parse(raw);
            return Array.isArray(decoded) ? decoded : [];
        } catch {
            // Fallback for invalid JSON that looks like an array
            return raw.replace(/[\[\]\"]/g, '').split(',').map(s => s.trim()).filter(Boolean);
        }
    }
    return raw.split(',').map(s => s.trim()).filter(Boolean);
};

interface VideoStoryProps {
    // Accept any product shape as long as it may contain a videoUrl field.
    product?: any | null;
}

const StoryModal: React.FC<{ videos: string[]; startIndex: number; onClose: () => void; }> = ({ videos, startIndex, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(startIndex);
    const videoRef = useRef<HTMLVideoElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);

    const goToNext = useCallback(() => {
        setCurrentIndex(prev => (prev + 1) % videos.length);
    }, [videos.length]);

    const goToPrev = () => {
        setCurrentIndex(prev => (prev - 1 + videos.length) % videos.length);
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleVideoEnd = () => {
            goToNext();
        };

        const handleTimeUpdate = () => {
            if (video.duration && progressRef.current) {
                const progress = (video.currentTime / video.duration) * 100;
                progressRef.current.style.width = `${progress}%`;
            }
        };

        video.addEventListener('ended', handleVideoEnd);
        video.addEventListener('timeupdate', handleTimeUpdate);

        // Reset progress and play
        if (progressRef.current) progressRef.current.style.width = '0%';
        video.currentTime = 0;
        video.play().catch(e => console.error("Autoplay was prevented:", e));

        return () => {
            video.removeEventListener('ended', handleVideoEnd);
            video.removeEventListener('timeupdate', handleTimeUpdate);
        };

    }, [currentIndex, goToNext]);

    // Handle swipe gestures
    const touchStartRef = useRef(0);
    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartRef.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e: React.TouchEvent) => {
        const touchEnd = e.changedTouches[0].clientX;
        const delta = touchEnd - touchStartRef.current;
        if (Math.abs(delta) > 50) { // Swipe threshold
            if (delta < 0) goToNext(); else goToPrev();
        }
    };


    return (
        <div id="Vediolink--anchor" className="fixed inset-0 z-[1005] bg-black/80 backdrop-blur-sm flex items-center justify-center" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            {/* Main story container with phone-like aspect ratio */}
            <div className="relative w-full h-full max-w-[450px] md:max-h-[800px] bg-black md:rounded-2xl overflow-hidden shadow-2xl flex flex-col">

                {/* Progress Bars */}
                <div className="absolute top-2 left-2 right-2 flex gap-1 z-40">
                    {videos.map((_, i) => (
                        <div key={i} className="h-1 bg-white/30 rounded-full flex-1">
                            <div
                                className="h-full bg-white rounded-full"
                                style={{ width: i < currentIndex ? '100%' : (i === currentIndex ? '0%' : '0%'), transition: i === currentIndex ? 'width 0.1s linear' : 'none' }}
                                ref={i === currentIndex ? progressRef : null}
                            ></div>
                        </div>
                    ))}
                </div>

                {/* Header */}
                <div className="absolute top-5 left-4 right-4 z-40 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                    </div>
                    <button onClick={onClose} className="text-white text-2xl font-bold">✕</button>
                </div>

                {/* Video Content */}
                <div className="flex-1 w-full h-full flex items-center justify-center">
                    <video
                        ref={videoRef}
                        key={currentIndex}
                        className="w-full h-full object-contain"
                        playsInline
                        autoPlay
                        preload="auto"
                    >
                        <source src={getImageUrl(videos[currentIndex])} type="video/mp4" />
                        Votre navigateur ne supporte pas la vidéo.
                    </video>
                </div>

                {/* Footer / Reply Bar */}
                <div className="absolute bottom-4 left-4 right-4 z-40">
                    <div className="w-full h-12 bg-transparent border border-white/50 rounded-full px-4 flex items-center">
                        <span className="text-white/70 text-sm">Envoyer un message...</span>
                    </div>
                </div>

                {/* Navigation */}
                <button onClick={goToPrev} className="absolute left-0 top-0 bottom-0 w-1/3 z-30"></button>
                <button onClick={goToNext} className="absolute right-0 top-0 bottom-0 w-1/3 z-30"></button>
            </div>
        </div>
    );
};

const VideoStory: React.FC<VideoStoryProps> = ({ product }) => {
    const videoLinks = useMemo(() => parseVideoLinks(product?.videoUrl), [product?.videoUrl]);
    const [isModalOpen, setModalOpen] = useState(false);
    const [activeVideoIndex, setActiveVideoIndex] = useState(0);

    const openModal = (index: number) => {
        setActiveVideoIndex(index);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    if (videoLinks.length === 0) {
        return null; // Don't render anything if there are no videos
    }

    return (
        <div className="max-w-3xl mx-auto my-4">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {videoLinks.map((link, i) => (
                    <div key={i} className="rounded-lg overflow-hidden shadow-md bg-gray-100 w-full aspect-w-9 aspect-h-16 relative cursor-pointer group" onClick={() => openModal(i)}>
                        <video
                            className="w-full h-full object-cover"
                            playsInline
                            muted
                            autoPlay
                            loop
                            preload="metadata"
                            poster="/assets/images/placeholder.png"
                        >
                            <source src={getImageUrl(link)} type="video/mp4" />
                        </video>
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-all flex items-center justify-center">
                            <span className="w-10 h-10 bg-white/30 text-white rounded-full flex items-center justify-center shadow-lg backdrop-blur-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20"><path d="M6.5 5.5v9l7-4.5-7-4.5z" /></svg>
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <StoryModal
                    videos={videoLinks}
                    startIndex={activeVideoIndex}
                    onClose={closeModal}
                />
            )}
        </div>
    );
};

export default VideoStory;
